/**
 * Utility functions for checking user permissions
 */

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} permissionKey - Permission key to check
 * @param {boolean} permissionBypass - Whether to bypass permission checks globally
 * @returns {boolean} - True if user has the permission or bypass is enabled
 */
export const hasPermission = (userPermissions, permissionKey, permissionBypass = false) => {
  // If bypass is enabled, grant all permissions
  if (permissionBypass) return true;
  
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  
  // Check for wildcard permissions first
  if (hasWildcardPermission(userPermissions, permissionKey)) {
    return true;
  }
  
  // Handle both object format {key: 'permission'} and string format ['permission']
  return userPermissions.some(permission => {
    const key = typeof permission === 'string' ? permission : permission.key;
    return key === permissionKey;
  });
};

/**
 * Check if user has at least one of the specified permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} permissionKeys - Array of permission keys to check
 * @param {boolean} permissionBypass - Whether to bypass permission checks globally
 * @returns {boolean} - True if user has at least one of the permissions or bypass is enabled
 */
export const hasAtLeastOnePermission = (userPermissions, permissionKeys, permissionBypass = false) => {
  // If bypass is enabled, grant all permissions
  if (permissionBypass) return true;
  
  if (!userPermissions || !Array.isArray(userPermissions) || !permissionKeys || !Array.isArray(permissionKeys)) return false;
  
  // Check for global wildcard first
  if (hasGlobalWildcard(userPermissions)) {
    return true;
  }
  
  return permissionKeys.some(key => hasPermission(userPermissions, key));
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} userPermissions - Array of permission keys to check
 * @param {Array} permissionKeys - Array of permission keys to check
 * @param {boolean} permissionBypass - Whether to bypass permission checks globally
 * @returns {boolean} - True if user has all of the permissions or bypass is enabled
 */
export const hasAllPermissions = (userPermissions, permissionKeys, permissionBypass = false) => {
  // If bypass is enabled, grant all permissions
  if (permissionBypass) return true;
  
  if (!userPermissions || !Array.isArray(userPermissions) || !permissionKeys || !Array.isArray(permissionKeys)) return false;
  
  // Check for global wildcard first
  if (hasGlobalWildcard(userPermissions)) {
    return true;
  }
  
  return permissionKeys.every(key => hasPermission(userPermissions, key));
};

/**
 * Get permissions by module
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} module - Module name to filter by
 * @returns {Array} - Filtered permissions for the module
 */
export const getPermissionsByModule = (userPermissions, module) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return [];
  return userPermissions.filter(permission => {
    const permissionModule = typeof permission === 'string' ? permission.split('.')[0] : permission.module;
    return permissionModule === module;
  });
};

/**
 * Check if user has permission for a specific action on a resource
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} resource - Resource name (e.g., 'user', 'company')
 * @param {string} action - Action name (e.g., 'create', 'read', 'update', 'delete')
 * @param {boolean} permissionBypass - Whether to bypass permission checks globally
 * @returns {boolean} - True if user has the permission or bypass is enabled
 */
export const hasResourcePermission = (userPermissions, resource, action, permissionBypass = false) => {
  // If bypass is enabled, grant all permissions
  if (permissionBypass) return true;
  
  if (!userPermissions || !Array.isArray(userPermissions) || !resource || !action || typeof resource !== 'string' || typeof action !== 'string') return false;
  
  // Check for global wildcard first
  if (hasGlobalWildcard(userPermissions)) {
    return true;
  }
  
  const permissionKey = `${resource}.${action}`;
  return hasPermission(userPermissions, permissionKey);
};

/**
 * Check if user has wildcard permissions that would grant access to everything
 * @param {Array} userPermissions - Array of user permissions
 * @returns {boolean} - True if user has wildcard permissions
 */
export const hasGlobalWildcard = (userPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  
  // Handle both object format {key: '*'} and string format ['*']
  const hasWildcard = userPermissions.some(permission => {
    const permissionKey = typeof permission === 'string' ? permission : permission.key;
    return permissionKey === '*' || permissionKey === 'admin.*' || permissionKey === 'admin';
  });
  
  return hasWildcard;
};

/**
 * Check if user has wildcard permission for a specific module/resource
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} permissionKey - Permission key to check
 * @param {boolean} permissionBypass - Whether to bypass permission checks globally
 * @returns {boolean} - True if user has wildcard permission for this resource or bypass is enabled
 */
export const hasWildcardPermission = (userPermissions, permissionKey, permissionBypass = false) => {
  // If bypass is enabled, grant all permissions
  if (permissionBypass) return true;
  
  if (!userPermissions || !Array.isArray(userPermissions) || !permissionKey) return false;
  
  // Check for global wildcard
  if (hasGlobalWildcard(userPermissions)) {
    return true;
  }
  
  // Check for module-level wildcard (e.g., "user.*" would grant "user.create")
  const module = permissionKey.split('.')[0];
  const moduleWildcard = `${module}.*`;
  
  const hasModuleWildcard = userPermissions.some(permission => {
    const key = typeof permission === 'string' ? permission : permission.key;
    return key === moduleWildcard || key === module;
  });
  
  return hasModuleWildcard;
};

/**
 * Check if user has admin permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {boolean} permissionBypass - Whether to bypass permission checks globally
 * @returns {boolean} - True if user has admin permissions or bypass is enabled
 */
export const isAdmin = (userPermissions, permissionBypass = false) => {
  // If bypass is enabled, grant admin access
  if (permissionBypass) return true;
  
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  return userPermissions.some(permission => {
    const key = typeof permission === 'string' ? permission : permission.key;
    return key === 'admin' ||
      key === 'admin.*' ||
      key === '*' ||
      key.includes('admin.');
  });
};
